import { useRef, useCallback } from 'react';

/**
 * Hook para gerenciar fila sequencial de atualizações de grupos
 * Evita race conditions processando uma atualização por vez
 * 
 * @param {Function} funcaoAtualizacao - (grupoSnapshot, baseLegalId) => Promise — compõe projeto deriva de baseLegalId no caller
 * @returns {Object} { adicionarFila, obterEstadoFila }
 */
export const useFilaAtualizacoesGrupos = (funcaoAtualizacao) => {
  // Fila de atualizações pendentes
  const filaRef = useRef([]);
  
  // Flag para controlar se está processando
  const processandoRef = useRef(false);
  
  // Callbacks pendentes (resolve/reject das Promises)
  const callbacksRef = useRef([]);

  /**
   * Processa a fila de atualizações sequencialmente
   */
  const processarFila = useCallback(async () => {
    // Se já está processando ou não há nada na fila, retorna
    if (processandoRef.current || filaRef.current.length === 0) {
      return;
    }

    // Marca como processando
    processandoRef.current = true;

    // Processa todas as atualizações da fila, uma por vez
    while (filaRef.current.length > 0) {
      const atualizacao = filaRef.current.shift();
      const callbacks = callbacksRef.current.shift();

      try {
        // Passa o snapshot do grupo (valores no momento do clique) para evitar enviar conta errada à API
        const resultado = await funcaoAtualizacao(
          atualizacao.grupoSnapshot,
          atualizacao.baseLegalId
        );

        // Resolve a Promise com o resultado
        if (callbacks?.resolve) {
          callbacks.resolve(resultado);
        }
      } catch (error) {
        // Rejeita a Promise com o erro
        if (callbacks?.reject) {
          callbacks.reject(error);
        }
      }
    }

    // Marca como não processando
    processandoRef.current = false;
  }, [funcaoAtualizacao]);

  /**
   * Adiciona uma atualização à fila.
   * Guarda um snapshot (schema_empresa, id_conta_contabil) no momento do clique para que a API
   * seja sempre chamada com a conta correta, mesmo com re-renders ou referências reutilizadas.
   *
   * @param {Object} grupo - Objeto do grupo (deve ter schema_empresa e id_conta_contabil)
   * @param {number|null} baseLegalId - ID da base legal (compõe projeto deriva: true se houver id)
   * @returns {Promise} Promise que resolve com o resultado da atualização
   */
  const adicionarFila = useCallback((grupo, baseLegalId) => {
    return new Promise((resolve, reject) => {
      // Snapshot dos campos necessários para a API (evita enviar conta errada por referência alterada)
      const grupoSnapshot = {
        schema_empresa: grupo?.schema_empresa != null ? String(grupo.schema_empresa).trim() : undefined,
        id_conta_contabil: grupo?.id_conta_contabil != null ? Number(grupo.id_conta_contabil) : undefined,
      };

      filaRef.current.push({
        grupoSnapshot,
        baseLegalId,
      });

      callbacksRef.current.push({ resolve, reject });

      processarFila();
    });
  }, [processarFila]);

  /**
   * Retorna informações sobre o estado da fila (útil para debug)
   * 
   * @returns {Object} { tamanho, processando }
   */
  const obterEstadoFila = useCallback(() => {
    return {
      tamanho: filaRef.current.length,
      processando: processandoRef.current,
    };
  }, []);

  return {
    adicionarFila,
    obterEstadoFila,
  };
};

